import { useAuthStore } from "@/state/authStore";
import { authService } from "./authService";

/**
 * Auth Hooks - Business logic for authentication
 */

export function useAuth() {
  const { account, subscription, isAuthenticated, isLoading } = useAuthStore();

  return {
    account,
    subscription,
    isAuthenticated,
    isLoading,
    hasActiveSubscription:
      subscription?.status === "active" || subscription?.status === "trialing",
  };
}

export function useAuthActions() {
  const { setAccount, setSubscription, setLoading, logout } = useAuthStore();

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { account, subscription } = await authService.signIn(
        email,
        password,
      );
      setAccount(account);
      setSubscription(subscription);
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Login failed";
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email: string, password: string, captchaToken?: string) => {
    try {
      setLoading(true);
      const { account, subscription, requiresEmailConfirmation } =
        await authService.signUp(email, password, captchaToken);
      // Only put account in store when we have an active session.
      // If email confirmation is required there is no session yet.
      if (!requiresEmailConfirmation) {
        setAccount(account);
        setSubscription(subscription);
      }
      return { success: true as const, requiresEmailConfirmation };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Signup failed";
      return {
        success: false as const,
        error: message,
        requiresEmailConfirmation: false,
      };
    } finally {
      setLoading(false);
    }
  };

  const signout = async () => {
    try {
      await authService.signOut();
      logout();
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Logout failed";
      return { success: false, error: message };
    }
  };

  const refreshSubscription = async (): Promise<boolean> => {
    const sessionData = await authService.getSession();
    if (sessionData) {
      setSubscription(sessionData.subscription);
      const { status } = sessionData.subscription;
      return status === "active" || status === "trialing";
    }
    return false;
  };

  const loginWithGoogle = async () => {
    try {
      await authService.signInWithGoogle();
      // Browser redirects to Google — nothing to return
    } catch (error) {
      const message = error instanceof Error ? error.message : "Google sign-in failed";
      return { success: false, error: message };
    }
  };

  return {
    login,
    signup,
    signout,
    loginWithGoogle,
    refreshSubscription,
  };
}
